import type { CanParameters } from '@casl/ability';
import { AbilityBuilder, PureAbility, fieldPatternMatcher } from '@casl/ability';
import type { AbilityQuery, RuleToQueryConverter } from '@casl/ability/extra';
import { permittedFieldsOf, rulesToQuery } from '@casl/ability/extra';
import type { AppConfig } from '@plato/app-meta/app-config-type';
import { pick } from 'lodash-es';
import { z } from 'zod';
import { applyAclConfig } from '../config/acl/apply-acl-config.ts';
import { conditionToZodSchema } from '../features/acl/condition-to-zod-schema.ts';
import { conditionsMatcher } from '../features/acl/conditions-matcher.ts';
import type {
    Abilities,
    AppAbility,
    ListSubjectKey,
    ReadableSubjectKey,
    SubjectActions,
    SubjectConditions,
    SubjectKey,
    SubjectObject,
    SubjectPayload,
    SubjectType,
    TypeSafeAllow,
    TypeSafeRestrict,
} from '../types/acl-types.ts';

export class AuthClient {
    public ability: AppAbility;
    public config: AppConfig | undefined;

    public constructor(config: AppConfig | undefined) {
        this.config = config;

        const { can, cannot, build } = new AbilityBuilder<AppAbility>(PureAbility);
        const allowCalls = new Map<SubjectKey, Set<SubjectActions>>();

        const allow = ((action, subject, field, conditions) => {
            if (subject === 'all') {
                throw new Error('Cannot allow all subjects');
            }

            const actions = allowCalls.get(subject) ?? new Set();

            if (actions.has(action) || actions.has('manage') || (action === 'manage' && actions.size > 0)) {
                throw new Error(`Action ${action} is already allowed on subject ${subject}`);
            }

            actions.add(action);
            allowCalls.set(subject, actions);

            can(action as never, subject as never, field as never, conditions as never);
        }) as TypeSafeAllow;

        if (config) {
            applyAclConfig(
                {
                    allow,
                    restrict: cannot as TypeSafeRestrict,
                },
                config,
            );
        }

        this.ability = build({
            detectSubjectType: (object): SubjectKey =>
                'kind' in object ? object.kind : (object as SubjectKey),
            conditionsMatcher,
            fieldMatcher: fieldPatternMatcher,
        });
    }

    public subject = <TKey extends SubjectKey>(
        type: TKey,
        payload: SubjectPayload<TKey>,
    ): SubjectObject<TKey> => {
        return {
            ...(payload as object),
            kind: type,
        } as SubjectObject<TKey>;
    };

    public can = <TKey extends SubjectKey>(
        action: SubjectActions<TKey>,
        subject: SubjectType<TKey>,
        field?: keyof SubjectPayload<TKey>,
    ) => {
        return this.ability.can(...([action, subject, field] as unknown as CanParameters<Abilities>));
    };

    public getAllDefaultFilters = <K extends ListSubjectKey>(subject: K): SubjectConditions<K> => {
        const rule = this.ability.relevantRuleFor('list', subject);

        if (!rule) {
            throw new Error(`Cannot perform "list" on subject ${subject}`);
        }

        return rule.conditions ?? {};
    };

    public getRelevantConditionsTo<K extends SubjectKey>(
        action: SubjectActions<K>,
        subject: K,
    ): SubjectConditions<K> | undefined {
        const rule = this.ability.relevantRuleFor(action as never, subject);

        return rule?.conditions as SubjectConditions<K> | undefined;
    }

    public getRelevantMandatoryFilters = <K extends ReadableSubjectKey>(
        subject: K,
        filters: SubjectConditions<K>,
    ): SubjectConditions<K> => {
        const relevantConditions = this.rulesToConditions(subject);

        if (relevantConditions.length === 0) {
            return {};
        }

        for (const conditions of relevantConditions) {
            const schema = conditionToZodSchema(conditions);

            try {
                schema.parse(filters);
                return conditions;
            } catch {
                // Try another one
            }
        }

        throw new Error(`Filters are not valid for subject "${subject}"`);
    };

    public validateFilters = <K extends ReadableSubjectKey, F extends SubjectConditions<K>>(
        subject: K,
        filters: F,
    ): F & SubjectConditions<K> => {
        const conditions = this.rulesToConditions(subject);

        if (conditions.length === 0) {
            return filters;
        }

        const schema =
            conditions.length === 1
                ? conditionToZodSchema(conditions[0])
                : z.union(
                      conditions.map(conditionToZodSchema) as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]],
                  );

        return schema
            .catch({
                ...filters,
                ...conditions[0],
            })
            .parse(filters) as F & SubjectConditions<K>;
    };

    private rulesToConditions<K extends ReadableSubjectKey>(subject: K): SubjectConditions<K>[] {
        const action = 'list' as SubjectActions<K>;
        const query = this.rulesToQuery(action, subject, (rule) => rule.conditions);

        if (!query) {
            throw new Error(`Cannot perform "${action}" on subject ${subject}`);
        }

        if (query.$and) {
            throw new Error(`Key "$and" is not supported by AuthClient (${subject})`);
        }

        return (query.$or ?? []) as SubjectConditions<K>[];
    }

    private rulesToQuery<K extends SubjectKey, R>(
        action: SubjectActions<K>,
        subject: K,
        convert: RuleToQueryConverter<AppAbility, R>,
    ): AbilityQuery<R> | null {
        return rulesToQuery<AppAbility, R>(this.ability, action, subject, convert);
    }

    public permittedFieldsOf<K extends SubjectKey>(
        action: SubjectActions<K>,
        subject: K,
        subjectFields: string[] = [],
    ) {
        /**
         * We need to hack the permittedFieldsOf function to allow the fieldsFrom option
         * to be a function that returns the fields from the subjectFields
         *
         * It is mandatory at least one permission to be allowed eg: allow('read', '<Subject>')
         */
        const fields = permittedFieldsOf<AppAbility>(this.ability, action, subject, {
            fieldsFrom: (fieldsFrom) => fieldsFrom.fields || subjectFields,
        });

        return fields;
    }

    public permittedValues<K extends SubjectKey, R extends SubjectPayload<K>>(subject: K, values: R): R;
    public permittedValues<K extends SubjectKey, R extends SubjectPayload<K>>(subject: K, values: R[]): R[];
    public permittedValues(subject: SubjectKey, values: unknown): unknown {
        const firstValue: unknown = Array.isArray(values) ? values[0] : values;

        const availableFields = Object.keys(firstValue ?? {});
        const fields = this.permittedFieldsOf('read', subject, availableFields);

        if (Array.isArray(values)) {
            return values.map((value) => pick(value, fields));
        } else {
            return pick(values, fields);
        }
    }
}
